import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProfile, updateUserDetails, updatePassword } from "../redux/actions/profileActions";
import { logoutEmployee } from "../redux/actions/authActions";
import { useNavigate } from "react-router-dom";

const ProfilePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: profileData, loading, error } = useSelector(
    (state) => state.profile
  );

  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    name: '',
    email: '',
    dob: '',
    photo: null
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  
  // Password reset modal state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [passwordError, setPasswordError] = useState('');

  // Initialize editedData when profileData is loaded
  useEffect(() => {
    if (profileData) {
      setEditedData({
        name: profileData.name || '',
        email: profileData.email || '',
        dob: profileData.dob || '',
        photo: null
      });
      setPhotoPreview(null);
    }
  }, [profileData]);

  useEffect(() => {
    dispatch(fetchProfile());
  }, [dispatch]);

  const handleLogout = () => {
    dispatch(logoutEmployee());
    navigate("/login");
  };

  const handleEditClick = () => {
    setIsEditing(!isEditing);
    // Reset edited data to original when canceling edit
    if (isEditing) {
      setEditedData({
        name: profileData.name || '',
        email: profileData.email || '',
        dob: profileData.dob || '',
        photo: null
      });
      setPhotoPreview(null);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditedData(prev => ({
        ...prev,
        photo: file
      }));
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveChanges = () => {
    const formData = new FormData();
    formData.append('name', editedData.name);
    formData.append('email', editedData.email);
    formData.append('dob', editedData.dob);
    if (editedData.photo) {
      formData.append('photo', editedData.photo);
    }
    
    dispatch(updateUserDetails(formData));
    console.log('Saving changes:', {
      name: editedData.name,
      email: editedData.email,
      dob: editedData.dob,
      photo: editedData.photo ? 'File selected' : 'No file'
    });
    setIsEditing(false);
  };

  // Password modal handlers
  const handlePasswordModalOpen = () => {
    setIsPasswordModalOpen(true);
    setPasswordData({
      new_password: '',
      confirm_password: ''
    });
    setPasswordError('');
  };

  const handlePasswordModalClose = () => {
    setIsPasswordModalOpen(false);
    setPasswordData({
      new_password: '',
      confirm_password: ''
    });
    setPasswordError('');
  };

  const handlePasswordInputChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (passwordError) {
      setPasswordError('');
    }
  };

  const handlePasswordSubmit = () => {
    // Validation
    if (!passwordData.new_password || !passwordData.confirm_password) {
      setPasswordError('Both fields are required');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }

    // Dispatch the updatePassword action
    const passwordObj = {
      id: profileData.id ,
      new_password: passwordData.new_password,
      confirm_password: passwordData.confirm_password
    };

    dispatch(updatePassword(passwordObj));
    handlePasswordModalClose();
  };

  // Check if error is related to invalid token
  const isInvalidTokenError = () => {
    if (!error) return false;
    const errorMsg = error.toLowerCase();
    return errorMsg.includes('invalid token') || 
           errorMsg.includes('unauthorized') || 
           errorMsg.includes('token expired') ||
           errorMsg.includes('authentication failed') ||
           errorMsg.includes('401');
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-sky-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600 mx-auto mb-4"></div>
          <p className="text-sky-700 font-medium">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Show error state with logout option for invalid token
  if (error) {
    return (
      <div className="min-h-screen bg-sky-100 flex flex-col">
        {/* Header */}
        <div className="bg-sky-500 text-white px-4 pt-4 pb-2">
          <h1 className="text-center text-lg font-semibold">My Profile</h1>
        </div>

        {/* Error Content */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md text-center">
            {isInvalidTokenError() ? (
              <>
                {/* Invalid Token Error */}
                <div className="mb-6">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Session Expired</h3>
                  <p className="text-gray-600 mb-4">
                    Your login session has expired. Please log in again to continue.
                  </p>
                  <p className="text-sm text-red-600 bg-red-50 p-2 rounded border">
                    {error}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white py-3 px-4 rounded-lg font-semibold transition-colors"
                >
                  Go to Login
                </button>
              </>
            ) : (
              <>
                {/* General Error */}
                <div className="mb-6">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Something went wrong</h3>
                  <p className="text-sm text-red-600 bg-red-50 p-2 rounded border mb-4">
                    {error}
                  </p>
                </div>
                <div className="flex flex-col space-y-3">
                  <button
                    onClick={() => dispatch(fetchProfile())}
                    className="w-full bg-sky-600 hover:bg-sky-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show message if no profile data
  if (!profileData) {
    return (
      <div className="min-h-screen bg-sky-100 flex flex-col">
        {/* Header */}
        <div className="bg-sky-500 text-white px-4 pt-4 pb-2">
          <h1 className="text-center text-lg font-semibold">My Profile</h1>
        </div>

        {/* No Data Content */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Profile Data</h3>
              <p className="text-gray-600 mb-4">
                Unable to load your profile information.
              </p>
            </div>
            <div className="flex flex-col space-y-3">
              <button
                onClick={() => dispatch(fetchProfile())}
                className="w-full bg-sky-600 hover:bg-sky-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
              >
                Retry
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const displayData = isEditing ? editedData : profileData;

  return (
    <div className="min-h-screen bg-sky-100 flex flex-col">
      {/* Header */}
      <div className="bg-sky-500 text-white px-4 pt-4 pb-2">
        <h1 className="text-center text-lg font-semibold">My Profile</h1>
      </div>

      {/* Profile Picture + Name */}
      <div className="bg-slate-800 text-white flex flex-col items-center py-6">
        <div className="relative">
          <div className="w-24 h-24 rounded-full border-4 border-green-500 overflow-hidden mb-2">
            <img
              src={
                photoPreview || profileData.photo || "https://via.placeholder.com/100"
              }
              alt="Profile"
              className="w-full h-full object-cover"
            />
          </div>
          {isEditing && (
            <div className="absolute -bottom-2 -right-2">
              <label htmlFor="photo-upload" className="cursor-pointer">
                <div className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-2 shadow-lg transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              </label>
              <input
                id="photo-upload"
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
              />
            </div>
          )}
        </div>
        <h2 className="text-lg font-semibold">{displayData.name}</h2>
        {isEditing && (
          <p className="text-xs text-gray-300 mt-1">Click camera icon to change photo</p>
        )}
      </div>

      {/* Form Fields */}
      <div className="flex-1 p-4 flex flex-col items-center">
        <div className="w-full max-w-md bg-white p-4 rounded-lg shadow space-y-4">
          {/* Editable Name Field */}
          <div>
            <label className="text-gray-600 text-sm">Name</label>
            <input
              type="text"
              value={displayData.name}
              readOnly={!isEditing}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`w-full border rounded px-3 py-2 ${
                isEditing ? 'bg-white border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500' : 'bg-gray-100'
              }`}
            />
          </div>

          {/* Editable Email Field */}
          <div>
            <label className="text-gray-600 text-sm">Email</label>
            <input
              type="email"
              value={displayData.email}
              readOnly={!isEditing}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`w-full border rounded px-3 py-2 ${
                isEditing ? 'bg-white border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500' : 'bg-gray-100'
              }`}
            />
          </div>

          {/* Non-editable Phone Field */}
          <div>
            <label className="text-gray-600 text-sm">Phone</label>
            <input
              type="text"
              value={profileData.phone}
              readOnly
              className="w-full bg-gray-100 border rounded px-3 py-2"
            />
          </div>

          {/* Editable DOB Field */}
          <div>
            <label className="text-gray-600 text-sm">DOB</label>
            <input
              type="date"
              value={displayData.dob}
              readOnly={!isEditing}
              onChange={(e) => handleInputChange('dob', e.target.value)}
              className={`w-full border rounded px-3 py-2 ${
                isEditing ? 'bg-white border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500' : 'bg-gray-100'
              }`}
            />
          </div>

          {/* Non-editable Municipality Field */}
          <div>
            <label className="text-gray-600 text-sm">Municipality</label>
            <input
              type="text"
              value={profileData.municipality}
              readOnly
              className="w-full bg-gray-100 border rounded px-3 py-2"
            />
          </div>

          {/* Non-editable Designation Field */}
          <div>
            <label className="text-gray-600 text-sm">Designation</label>
            <input
              type="text"
              value={profileData.designation}
              readOnly
              className="w-full bg-gray-100 border rounded px-3 py-2"
            />
          </div>

          {/* Non-editable District Field */}
          <div>
            <label className="text-gray-600 text-sm">District</label>
            <input
              type="text"
              value={profileData.district}
              readOnly
              className="w-full bg-gray-100 border rounded px-3 py-2"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="w-full max-w-md mt-6 space-y-4 mb-24">
          {isEditing ? (
            <div className="flex space-x-4">
              <button 
                className="flex-1 bg-green-600 text-white py-2 rounded font-semibold"
                onClick={handleSaveChanges}
              >
                Save Changes
              </button>
              <button 
                className="flex-1 bg-gray-600 text-white py-2 rounded font-semibold"
                onClick={handleEditClick}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button 
              className="w-full bg-green-600 text-white py-2 rounded font-semibold"
              onClick={handleEditClick}
            >
              Edit Profile
            </button>
          )}
          
          <button 
            className="w-full bg-pink-400 text-white py-2 rounded font-semibold"
            onClick={handlePasswordModalOpen}
          >
            Reset Password
          </button>
          <button
            className="w-full bg-orange-400 text-white py-2 rounded font-semibold"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Password Reset Modal */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Reset Password</h3>
                <button
                  onClick={handlePasswordModalClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => handlePasswordInputChange('new_password', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter new password"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => handlePasswordInputChange('confirm_password', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Confirm new password"
                  />
                </div>

                {passwordError && (
                  <div className="text-red-600 text-sm">{passwordError}</div>
                )}
              </div>

              <div className="flex space-x-4 mt-6">
                <button
                  onClick={handlePasswordSubmit}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md font-medium transition-colors"
                >
                  Update Password
                </button>
                <button
                  onClick={handlePasswordModalClose}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;